import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import formatUSD from '../../../../utils/formatUSD';
import { tieredPriceText } from './retouchingSelections';
import { RetouchingProduct } from './types';

export type RetouchingLevelCardVariant = 'card' | 'summary';

// A level among others stacks its illustration over its text, so a row of
// cards compares at a glance. The one product of a scoped visit instead lies
// across the page, illustration beside the text, so it reads as the subject
// of the page rather than as a lone option in a grid meant for three. Phones
// use the horizontal layout for both. A product with no illustration keeps a
// single column: the text must not fall into the picture's column.
const Card = styled.div<{
  $illustrated: boolean;
  $variant: RetouchingLevelCardVariant;
}>`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  /* A row of cards stretches each to the tallest; the content stays at the
     top rather than spreading into the spare height. */
  align-content: start;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 8px;

  ${(props) =>
    props.$illustrated &&
    props.$variant === 'summary' &&
    css`
      grid-template-columns: 200px minmax(0, 1fr);
      column-gap: 24px;
      align-items: start;
    `}

  @media (max-width: 767px) {
    padding: 12px;

    ${(props) =>
      props.$illustrated &&
      css`
        grid-template-columns: 88px minmax(0, 1fr);
        column-gap: 12px;
        align-items: start;
      `}
  }
`;

const Illustration = styled.img<{ $variant: RetouchingLevelCardVariant }>`
  display: block;
  width: 100%;
  max-height: ${(props) => (props.$variant === 'summary' ? '140px' : '120px')};
  object-fit: contain;
  object-position: ${(props) =>
    props.$variant === 'summary' ? 'left top' : 'center'};

  @media (max-width: 767px) {
    object-position: left top;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Name = styled.h4<{ $variant: RetouchingLevelCardVariant }>`
  margin: 0;
  font-size: ${(props) => (props.$variant === 'summary' ? '19px' : '17px')};
  line-height: 1.3;

  @media (max-width: 767px) {
    font-size: 17px;
  }
`;

const Price = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
`;

// Capped at a reading measure: across the page the description would
// otherwise run the full width in one long line.
const Description = styled.p`
  max-width: 64ch;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #4a4a4a;
`;

interface RetouchingLevelCardProps {
  product: RetouchingProduct;
  perImagePricing: boolean;
  variant?: RetouchingLevelCardVariant;
  // The level's product option choices, when it has any.
  options?: ReactNode;
  action?: ReactNode;
}

// One retouching level: what it does and what it costs. The photo picker
// below the cards is where a level is chosen, so the card carries no control
// of its own unless the studio still sells retouching as one flat add-on.
const RetouchingLevelCard: FC<RetouchingLevelCardProps> = ({
  product,
  perImagePricing,
  variant = 'card',
  options,
  action,
}) => (
  <Card
    data-testid={`retouching-level-${product.id}`}
    $illustrated={Boolean(product.image_thumbnail)}
    $variant={variant}
  >
    {product.image_thumbnail && (
      <Illustration src={product.image_thumbnail} alt="" $variant={variant} />
    )}
    <Body>
      <Name $variant={variant}>{product.name}</Name>
      <Price>
        {perImagePricing
          ? tieredPriceText(
              product.price,
              product.tiered_unit_prices_cents || [],
            )
          : formatUSD(product.price)}
      </Price>
      {product.description && <Description>{product.description}</Description>}
      {options}
      {action}
    </Body>
  </Card>
);

export default RetouchingLevelCard;
